/**
 * Interfeys tili (i18n) — o'zbek (lotin) va rus.
 *
 * YANGI NPM PAKET QO'SHILMAGAN (CLAUDE.md §3.4) — mavjud zustand ustiga qurilgan,
 * to'liq hajmi ~2KB. react-i18next kabi kutubxonalar bu loyiha uchun ortiqcha.
 *
 * ── Asosiy g'oya ───────────────────────────────────────────────────────────
 * Kalit sifatida O'ZBEKCHA MATNNING O'ZI ishlatiladi:
 *
 *     t('Tizimdan chiqish')   →  uz: "Tizimdan chiqish"
 *                                ru: "Выйти из системы"
 *
 * Sabab: (1) kod o'qishga tushunarli bo'lib qoladi, (2) tarjima topilmasa
 * o'zbekcha matn ko'rsatiladi — ekranda hech qachon `profile.logout.button`
 * kabi kalit yoki bo'sh joy chiqmaydi.
 *
 * ── Interpolatsiya ─────────────────────────────────────────────────────────
 *     t('{count} ta buyurtma', { count: 5 })   → "5 ta buyurtma"
 *
 * ── Ruscha ko'plik ─────────────────────────────────────────────────────────
 * Rus tilida 3 shakl bor (1 / 2-4 / 5+). Lug'atda `|` bilan ajratiladi va
 * `count` parametriga qarab avtomatik tanlanadi:
 *
 *     '{count} ta do'kon': '{count} магазин|{count} магазина|{count} магазинов'
 */

import { useSyncExternalStore } from 'react';
import { LANG_STORAGE_KEY } from '@/lib/constants';
import { ru } from './ru';

export type Lang = 'uz' | 'ru';

export const LANGUAGES: ReadonlyArray<{ code: Lang; label: string; nativeLabel: string; flag: string }> = [
  { code: 'uz', label: "O'zbekcha", nativeLabel: "O'zbekcha", flag: '🇺🇿' },
  { code: 'ru', label: 'Ruscha', nativeLabel: 'Русский', flag: '🇷🇺' },
];

export type Dictionary = Record<string, string>;

/** uz — manba til, lug'at kerak emas (kalitning o'zi matn). */
const DICTIONARIES: Record<Lang, Dictionary | null> = { uz: null, ru };

export type TParams = Record<string, string | number>;

// ── Holat ────────────────────────────────────────────────────────────────────

let currentLang: Lang = 'uz';
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): Lang {
  return currentLang;
}

function isLang(value: unknown): value is Lang {
  return value === 'uz' || value === 'ru';
}

/** Brauzer/Telegram tilidan boshlang'ich tilni taxmin qiladi. */
function detectLang(): Lang {
  const tgLang = (window.Telegram?.WebApp?.initDataUnsafe?.user as { language_code?: string } | undefined)
    ?.language_code;
  const candidates = [tgLang, ...(navigator.languages ?? [navigator.language])];
  for (const c of candidates) {
    if (!c) continue;
    const base = c.toLowerCase().split('-')[0];
    if (base === 'ru') return 'ru';
    if (base === 'uz') return 'uz';
    // Qozoq/qirg'iz/tojik foydalanuvchilar odatda ruschani tushunadi
    if (base === 'kk' || base === 'ky' || base === 'tg') return 'ru';
  }
  return 'uz';
}

export function getLang(): Lang {
  return currentLang;
}

export function setLang(lang: Lang): void {
  if (lang === currentLang) return;
  currentLang = lang;
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // Private rejimda localStorage yozib bo'lmasligi mumkin — til sessiya davomida ishlayveradi
  }
  document.documentElement.lang = lang;
  notify();
}

/** `main.tsx` da React render'dan oldin bir marta chaqiriladi. */
export function initI18n(): void {
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(LANG_STORAGE_KEY);
  } catch {
    saved = null;
  }
  currentLang = isLang(saved) ? saved : detectLang();
  document.documentElement.lang = currentLang;
}

// ── Tarjima ──────────────────────────────────────────────────────────────────

/** Rus tili ko'plik qoidasi: 1 / 2-4 / 5+ (11-14 → 5+ shakli). */
function pluralIndexRu(n: number): 0 | 1 | 2 {
  const abs = Math.abs(Math.trunc(n));
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod10 === 1 && mod100 !== 11) return 0;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 1;
  return 2;
}

function interpolate(template: string, params?: TParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in params ? String(params[key]) : match,
  );
}

/**
 * Matnni joriy tilga tarjima qiladi.
 * @param key O'zbekcha manba matn (u ayni paytda kalit ham).
 */
export function translate(key: string, params?: TParams): string {
  const dict = DICTIONARIES[currentLang];
  let text = dict?.[key] ?? key;

  // Ko'plik shakllari: "1 форма|2-4 форма|5+ форма"
  if (text.includes('|')) {
    const forms = text.split('|');
    const count = params?.count;
    if (typeof count === 'number' && forms.length === 3) {
      text = forms[pluralIndexRu(count)] ?? forms[0]!;
    } else {
      text = forms[0]!;
    }
  }

  return interpolate(text, params);
}

/**
 * React KOMPONENTLARIDA shu hook ishlatiladi — til o'zgarganda komponent
 * qayta render bo'lishi uchun. Komponentdan tashqarida (toast matni, xato
 * xabari, util funksiya) to'g'ridan-to'g'ri `translate()` chaqiriladi.
 *
 *     const t = useT();
 *     <span>{t('Bosh sahifa')}</span>
 */
export function useT(): (key: string, params?: TParams) => string {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return translate;
}

/** Joriy tilni kuzatuvchi hook (til tanlash UI'si uchun). */
export function useLang(): { lang: Lang; setLang: (lang: Lang) => void } {
  const lang = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { lang, setLang };
}

/** `Intl` / `dayjs` uchun to'liq lokal kodi. */
export function getLocale(): string {
  return currentLang === 'ru' ? 'ru-RU' : 'uz-Latn-UZ';
}

/**
 * Qisqa alias — komponentdan TASHQARIDA ham ishlaydi (util, event handler,
 * toast matni, modul darajasidagi funksiya).
 *
 * Til o'zgarganda butun daraxt `App.tsx` dagi `key={lang}` orqali qayta
 * mount bo'ladi, shuning uchun `t()` hook bo'lishi shart emas — bu 200 dan
 * ortiq faylga `useT()` chaqiruvini tarqatishdan ancha arzon va xatosizroq.
 *
 * DIQQAT: modul darajasidagi `const` ichida ISHLATMANG — u import vaqtida
 * bir marta hisoblanadi va til o'zgarishiga javob bermaydi. Bunday joylarda
 * o'zbekcha matnni kalit sifatida qoldiring va render paytida o'rang:
 *
 *     const TABS = [{ id: 'all', label: 'Barchasi' }];   // ✅ shunday qoldiring
 *     <span>{t(tab.label)}</span>                        // ✅ shu yerda o'rang
 */
export const t = translate;
